import { createContext, useContext, useReducer, type Dispatch, type ReactNode } from 'react';
import {
  createInitialOrderDraftState,
  orderDraftReducer,
  type OrderDraftAction,
  type OrderDraftState,
} from './orderDraft';

export type OrderDraftContextValue = {
  state: OrderDraftState;
  dispatch: Dispatch<OrderDraftAction>;
};

const OrderDraftContext = createContext<OrderDraftContextValue | undefined>(undefined);

/**
 * Provides the single source of truth for setup details + cart quantities
 * across the `/order/*` routes (see build-order spec §3 and §8). Pages read
 * `state` and dispatch cart/setup actions; nothing else holds this state.
 */
export function OrderDraftProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(orderDraftReducer, undefined, createInitialOrderDraftState);

  return (
    <OrderDraftContext.Provider value={{ state, dispatch }}>{children}</OrderDraftContext.Provider>
  );
}

export function useOrderDraft(): OrderDraftContextValue {
  const context = useContext(OrderDraftContext);
  if (!context) {
    throw new Error('useOrderDraft must be used within an OrderDraftProvider.');
  }

  return context;
}
